import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Divider from 'src/components/ui/Divider/Divider';
import Select, { Option } from 'src/components/ui/Select/Select';
import { Profile, profileDataPlaceholder } from 'src/services/profile';
import { isValidUserPassword, validateEmail } from 'src/utils/general';
import LogoHeadSettings from 'src/components/ui/LogoHeadSettings/LogoHeadSettings';
import Toggle from 'src/components/ui/Toggle/Toggle';
import { useTheme } from 'src/providers/ThemeProvider';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { useLogout } from 'src/services/session';
import Input from '../../ui/Input/Input';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import styles from './ProfileSettings.module.scss';
import { getLangCodes } from '../../../services/utility';
import { useProfile } from '../../../utils/ProfileService';

const LOGOUT_DELAY_MS = 3000;

const ProfileSettings = () => {
  const { t } = useTranslation();
  const [langOptions, setLangOptions] = useState<Option[]>([]);
  const [profileData, setProfileData] = useState<Profile>(profileDataPlaceholder);
  const [errors, setErrors] = useState({
    newPassword: '',
    newPasswordConfirmation: '',
  });
  const [isPasswordConfirmOpen, setIsPasswordConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const {
    profile,
    picture,
    loading,
    updateProfileData,
    updateProfilePicture,
    deleteProfilePicture,
    updateUserPasswords,
  } = useProfile();
  const { theme, setTheme } = useTheme();
  const logout = useLogout();

  const hasPasswordChange = () =>
    !!profileData.password &&
    !!profileData.newPassword &&
    !!profileData.newPasswordConfirmation &&
    profileData.newPassword === profileData.newPasswordConfirmation &&
    isValidUserPassword(profileData.newPassword);

  const hasProfileChange = () => {
    const { password, newPassword, newPasswordConfirmation, ...profileDataToCompare } = profileData;
    return JSON.stringify(profileDataToCompare) !== JSON.stringify(profile);
  };

  // Changing the password invalidates every token of the user on the backend,
  // so the profile is saved first and the session is ended right after.
  const handlePasswordChangeConfirm = async (confirmed: boolean) => {
    setIsPasswordConfirmOpen(false);
    if (!confirmed) return;

    if (hasProfileChange()) {
      await updateProfileData(profileData, true);
    }
    const success = await updateUserPasswords(profileData.password!, profileData.newPassword!);
    if (!success) return;

    // The toast container lives inside the authenticated layout, so the logout
    // waits until the user has had a moment to read the confirmation.
    setIsLoggingOut(true);
    toast.success(t('success.password_updated_logout'), { autoClose: LOGOUT_DELAY_MS });
    await new Promise<void>(resolve => {
      setTimeout(resolve, LOGOUT_DELAY_MS);
    });
    logout();
  };

  useEffect(() => {
    if (!profile || !profile.email) return;

    setProfileData(prev => {
      if (prev.email) return prev;
      return profile;
    });
  }, [profile]);

  const updateValue = (key: string, value: string) => {
    if (key) {
      setProfileData({ ...profileData, [key]: value });
    }
  };

  const updateToggles = (key: string, value: boolean) => {
    setProfileData({ ...profileData, [key]: value });
  };

  // Apply the theme immediately for a live preview, but treat it as an unsaved
  // change: it marks the form dirty (enabling the save button) and is only
  // persisted when the profile is saved.
  const handleDarkModeToggle = (value: boolean) => {
    setTheme(value ? 'dark' : 'light');
    updateToggles('is_in_dark_mode', value);
  };

  const handleNewPasswordChange = (value: string) => {
    updateValue('newPassword', value);

    if (value.length === 0) {
      setErrors(prev => ({ ...prev, newPassword: '' }));
      return;
    }

    if (!isValidUserPassword(value)) {
      setErrors(prev => ({
        ...prev,
        newPassword: t('settings.password_error_info'),
      }));
    } else {
      setErrors(prev => ({ ...prev, newPassword: '' }));
    }
  };

  const handleNewPasswordConfirmationChange = (value: string) => {
    updateValue('newPasswordConfirmation', value);

    if (value !== profileData.newPassword) {
      setErrors(prev => ({
        ...prev,
        newPasswordConfirmation: t('settings.password_do_not_match_error'),
      }));
    } else {
      setErrors(prev => ({ ...prev, newPasswordConfirmation: '' }));
    }
  };

  // Detects whether the form differs from the last saved profile. A pending
  // password entry always counts; otherwise the editable fields (incl. the
  // dark-mode toggle) are compared against the saved profile.
  const hasChanges = () => {
    if (!profile) return false;

    if (profileData.password || profileData.newPassword || profileData.newPasswordConfirmation) {
      return true;
    }

    const withoutPasswords = ({ password, newPassword, newPasswordConfirmation, ...rest }: Profile) => rest;
    return JSON.stringify(withoutPasswords(profileData)) !== JSON.stringify(withoutPasswords(profile));
  };

  const isSaveDisabled = () => {
    if (!profileData) return true;

    const { name, email } = profileData;
    const isInvalid = !(name ?? '').trim() || !(email ?? '').trim() || !validateEmail(email ?? '');

    return isInvalid || !hasChanges();
  };

  const getLangCodeOptions = async (): Promise<Option[]> => {
    const result = await getLangCodes();

    if (!result.data) {
      console.error('Error fetching language codes:', result.error);
      return [];
    }

    if (!Array.isArray(result.data)) {
      console.error('Unexpected API response:', result.data);
      return [];
    }

    return result.data.map(item => ({
      value: String(item.value),
      label: String(item.label),
    }));
  };

  useEffect(() => {
    const fetchLangCodes = async () => {
      const result = await getLangCodeOptions();
      setLangOptions(result);
    };

    fetchLangCodes();
  }, []);

  if (loading || !profile || !profile.email) {
    return (
      <div className="p-24 flex items-center justify-center min-h-[300px]">
        <span className="typo-text-14 text-faded">{t('general.loading')}</span>
      </div>
    );
  }

  return (
    <div className={classNames(styles.root)}>
      <LogoHeadSettings
        logo={picture}
        onSubmit={f => updateProfilePicture(f)}
        onDelete={() => deleteProfilePicture()}
        className="my-32"
        title={t('settings.my_profile')}
        description={t('settings.update_profile_info')}
      />
      <div className={classNames(styles.base, 'items-center')}>
        <span className="typo-text-16--bold text-full col-span-1">{t('settings.user_name')}</span>
        <Input
          value={profileData.name}
          error={!profileData?.name?.trim()}
          errorMessage={!profileData?.name?.trim() ? t('errors.field_required_error') : ''}
          onChange={e => updateValue('name', e.target.value)}
          placeholder={t('general.name')}
          className="grid-cols-1 lg:col-span-2"
        />
      </div>
      <div className={classNames(styles.base, 'items-center')}>
        <span className="typo-text-16--bold text-full col-span-1">{t('settings.phone_number')}</span>
        <Input
          value={profileData.phone}
          onChange={e => updateValue('phone', e.target.value)}
          placeholder={t('settings.phone')}
          className="col-span-1 lg:col-span-2"
        />
      </div>
      <Divider />
      <div className={classNames(styles.base)}>
        <span className="typo-text-16--bold text-full col-span-1 flex flex-col">{t('settings.email')}</span>
        <Input
          error={!profileData?.email?.trim() || !validateEmail(profileData.email)}
          errorMessage={
            !profileData?.email?.trim()
              ? t('errors.field_required_error')
              : !validateEmail(profileData.email)
                ? t('errors.invalid_email_error')
                : ''
          }
          value={profileData.email}
          onChange={e => updateValue('email', e.target.value)}
          placeholder={t('settings.email')}
          className="col-span-1 lg:col-span-2"
        />
      </div>
      <Divider />
      <div className={classNames(styles.base)}>
        <span className="typo-text-16--bold text-full col-span-1 flex flex-col">
          {t('settings.new_password')}
          <div className="typo-text-12 text-faded mt-4">{t('settings.new_password_info')}</div>
        </span>
        <div className="col-span-1 lg:col-span-2 flex flex-col gap-y-16">
          <Input
            value={profileData.password}
            onChange={e => updateValue('password', e.target.value)}
            label={t('settings.current_password')}
            placeholder=""
            icon="lock"
            secret
            type="password"
          />
          <Input
            value={profileData.newPassword}
            onChange={e => handleNewPasswordChange(e.target.value)}
            label={t('settings.new_password')}
            placeholder=""
            icon="lock"
            secret
            type="password"
            error={!!errors.newPassword}
            errorMessage={errors.newPassword}
          />
          <Input
            value={profileData.newPasswordConfirmation}
            onChange={e => handleNewPasswordConfirmationChange(e.target.value)}
            label={t('settings.confirm_new_password')}
            placeholder=""
            icon="lock"
            secret
            type="password"
            error={!!errors.newPassword}
            errorMessage={errors.newPasswordConfirmation}
          />
        </div>
      </div>
      <Divider />
      <div className={classNames(styles.base, 'items-center')}>
        <span className="typo-text-16--bold text-full col-span-1">{t('general.language')}</span>
        <div className="col-span-1 lg:col-span-2">
          {langOptions.length > 0 && profileData && profileData.lang && (
            <Select
              isMultiSelect={false}
              onChange={e => e && setProfileData({ ...profileData, lang: e.value })}
              options={langOptions}
              defaultValue={langOptions.find(option => option.value === profileData.lang) || undefined}
            />
          )}
        </div>
      </div>
      <Divider />
      <div className={classNames(styles.base, 'items-center')}>
        <span className="typo-text-16--bold text-full col-span-1 flex flex-col">
          {t('settings.dark_mode')}
          <div className="typo-text-12 text-faded mt-4">{t('settings.dark_mode_info')}</div>
        </span>
        <div className="col-span-1 lg:col-span-2">
          <Toggle checked={theme === 'dark'} onToggle={handleDarkModeToggle} />
        </div>
      </div>
      {/* <Divider /> */}
      {/* <div className={classNames(styles.base)}> */}
      {/*  <span className="typo-text-16--bold text-full col-span-1">{t('settings.notifications')}</span> */}
      {/*  <div className="col-span-1 lg:col-span-2 flex flex-col gap-y-24"> */}
      {/*    {notificationSettings.map(setting => ( */}
      {/*      <div key={setting.key} className="flex items-start gap-x-16"> */}
      {/*        <Toggle checked={setting.checked || false} onToggle={e => updateToggles(setting.key, e)} /> */}
      {/*        <div className="flex flex-col gap-y-8"> */}
      {/*          <h4 className="typo-text-14--bold text-full">{t(setting.title)}</h4> */}
      {/*        </div> */}
      {/*      </div> */}
      {/*    ))} */}
      {/*  </div> */}
      {/* </div> */}
      <Divider />
      <div className="mt-32 flex items-center justify-end gap-x-12">
        <Button
          title={t('general.save_changes')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          disabled={isSaveDisabled() || isLoggingOut}
          onClick={async () => {
            if (hasPasswordChange()) {
              setIsPasswordConfirmOpen(true);
              return;
            }
            if (hasProfileChange()) {
              await updateProfileData(profileData);
            }
          }}
        />
      </div>
      <ConfirmationModal
        isOpen={isPasswordConfirmOpen}
        title={t('settings.password_change_confirm_title')}
        description={t('settings.password_change_confirm_text')}
        onClose={handlePasswordChangeConfirm}
      />
    </div>
  );
};
export default ProfileSettings;
